'use client';

/**
 * The attribute treatment's **commit-on-blur save path — the one implementation**
 * (LF-6250).
 *
 * A surface with no Save button has no validate-before-send moment, so this
 * *is* its entire safety net: every field commits as the user leaves it, and
 * the only thing standing between a failed request and a silently lost
 * correction is the code in this file. `frontend/docs/attribute-treatment.md`
 * §2 is the contract it implements.
 *
 * ## Why it is a hook rather than lines inside the grid
 *
 * `LF-6240` moves the Contacts drawer onto the same treatment, and its scope
 * item 1 is an open decision about where the *grid component* should live —
 * widen `AttributeGrid` in place, promote the prototype's modules, or
 * widen then relocate. That decision is not this ticket's to make. But every
 * one of those options needs this save path, and the option that rebuilds the
 * grid from `components/prototypes/form-system/` would otherwise rebuild the
 * failure handling too — a second copy of the code whose failure mode is
 * *silently destroying the user's typing*. Extracting it means the duplication
 * is not available to be written: a new surface calls this hook, and the grid
 * architecture stays LF-6240's question.
 *
 * So this module knows **nothing about leads** — no `UpdateLeadInput`, no
 * `PostalAddress`, no field factories. It is generic in both the patch a field
 * produces and the shape its draft holds, and it moves with the grid if LF-6240
 * relocates it. It lives beside its only consumer today rather than in a
 * neutral home, because choosing that home is exactly the gate LF-6240 owns.
 *
 * ## The four defects it exists to fix
 *
 * Each was reproduced against the running app before it was fixed:
 *
 * 1. **A failure mid-`Tab`-chain destroyed an in-progress draft, silently.** The
 *    old failure handler re-opened the failed field unconditionally, and the
 *    draft is a *single* piece of state shared by whichever field is open — so
 *    re-opening field N overwrote whatever was being typed into field N+1 and
 *    moved focus backwards. See {@link AttributeCommit.commit}'s rejection
 *    handler for the rule that replaced it.
 * 2. **The failure banner was sticky.** The old code cleared it in exactly one
 *    place — inside a successful commit, *after* two early returns — so it
 *    survived `Escape`, survived opening another field, and survived re-opening
 *    the field that failed. It now clears whenever the user engages with the
 *    grid again.
 * 3. **Failure messages were raw wire strings** (`office must be a string`,
 *    `Failed to fetch`). {@link saveFailureMessage} maps them.
 * 4. **A refused value reverted silently** — indistinguishable from a save that
 *    worked. A refusal is now visible and `Tab` refuses to advance off it.
 *
 * ## What VALE-349 added: the save is no longer invisible either
 *
 * The four defects above are all about a save that came back **wrong**. Two more
 * are about a save whose *state* was never reported at all, and both fall out of
 * the same promise this path already holds:
 *
 * · **In flight.** A save still travelling after
 *   {@link ATTRIBUTE_SAVE_INDICATOR_DELAY_MS} puts its field's label into
 *   {@link AttributeCommit.saving}, and leaves it however the request settles.
 *   Under the threshold nothing is reported, so the common path stays silent.
 * · **Unsaved.** `onUnsavedChange` reports whether this field list is holding
 *   state the commit path could not store — a refusal, or a save past that same
 *   threshold — which is what lets a drawer stop before closing over it. It is
 *   called **synchronously**, and that is load-bearing rather than tidy: see the
 *   prop's own doc.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { DirectApiError, handleMutationError } from '@/lib/api/direct-client';
import { ATTRIBUTE_SAVE_INDICATOR_DELAY_MS } from './attribute-tokens';

/**
 * What a draft can hold: a string for text / number / date / select, or the
 * editor's own object for a composite field like an address.
 *
 * Deliberately not narrowed to either surface's address type — the enquiry
 * drawer's `PostalAddress` has every member optional and the prototype's
 * `StructuredAddress` does not, and this path never looks inside a draft.
 */
export type AttributeCommitDraft = string | object | null;

/**
 * Reads a sibling field's currently committed value by label — `undefined` if
 * no field in this block carries that label ([INVDRAW-3] / VALE-74, gap J).
 *
 * Scoped to exactly the `fields` this hook was given: `AttributeGrid` calls
 * `useAttributeCommit` once per `rows` block ([INVDRAW-2] / VALE-73), so a
 * validator sees every sibling in its own block and none from another block or
 * section. Reads `field.draft` — the prop the caller passes for the record's
 * current value, not the live in-progress edit of whichever field happens to
 * be open — so a sibling a validator reads is always the value last committed,
 * never an uncommitted keystroke.
 */
export type AttributeSiblingReader<TDraft> = (
  label: string
) => TDraft | undefined;

/**
 * The half of a field spec this hook reads. Both `AttributeSpec` and the
 * prototype's `AttributeSpec` satisfy it structurally, which is what lets one
 * save path serve both.
 */
export interface AttributeCommitField<
  TPatch,
  TDraft extends AttributeCommitDraft = AttributeCommitDraft
> {
  label: string;
  /** What the editor opens on, and what `Escape` restores. */
  draft: TDraft;
  /** The patch a committed draft produces, or `null` to refuse it. */
  toPatch: (draft: TDraft) => TPatch | null;
  /**
   * Refuses a committed draft **with a reason the user can act on**, keeping the
   * field open with the typing intact.
   *
   * Prefer this over refusing through `toPatch`: a `null` patch says only
   * *"no"*, so the grid can do no better than generic copy
   * ({@link REFUSED_WITHOUT_REASON}). A reason here names what is wrong with
   * the value in front of the reader.
   *
   * **Can read a sibling's current value** via the optional second parameter
   * — see {@link AttributeSiblingReader}. **Additive**: the parameter is
   * optional, so every validator written before it existed — a plain
   * `(draft) => string | null` — keeps type-checking and calling exactly as it
   * did.
   */
  validate?: (
    draft: TDraft,
    siblings?: AttributeSiblingReader<TDraft>
  ) => string | null;
  /**
   * **Commits to the host's view of the record rather than to the record**
   * (LF-6359 / [ATTR-9] §4).
   *
   * When present it *replaces* the send: this path calls it, clears the failure
   * line and reports the value accepted, and `toPatch` is never reached. It is
   * not a refusal and not a silent drop — the row genuinely has somewhere to put
   * the value, and that somewhere is not the server.
   *
   * **The Contacts drawer's `Contact type` row is the case it exists for, and
   * the shape is more general than it looks.** Production stores no contact-type
   * column: `isOrganisationContact` derives the type from whether
   * `organisationName` is set. So *"this is an organisation"* is not a statement
   * the API can accept on its own — there is nothing to write until the
   * organisation's name exists. Choosing `Organisation` therefore re-draws the
   * row list (the three name rows are replaced by a required `Organisation
   * name`) and waits; committing that name is what persists the type.
   *
   * The alternative was a `toPatch` returning `null`, which this hook has
   * defined since LF-6250 as a **visible refusal** — it would have put *"That
   * value cannot be saved"* over a switch that worked perfectly.
   */
  localCommit?: (draft: TDraft) => void;
  /**
   * **The row shows its value but never opens** — see
   * {@link AttributeSpec.readOnly}, which is where the behaviour is documented.
   *
   * This hook needs it for one thing only: `step` must hand `Tab` *over* such a
   * row rather than onto it, since there is no editor there to receive focus.
   * Nothing else here reads it — `commit` is never reached for a row that
   * cannot be opened.
   */
  readOnly?: boolean;
}

export interface AttributeCommitFailure {
  label: string;
  message: string;
  /**
   * Where the refusal came from, which decides what is allowed to clear it
   * (VALE-1476).
   *
   * `local` — this grid refused the DRAFT, synchronously, before anything was
   * sent (`validate`, or a `null` patch). It is about a value that only ever
   * existed in the editor, so putting the draft back the way it was found
   * makes it moot.
   *
   * `remote` — the server rejected a value that WAS sent and IS what the row
   * now holds. Reverting the editor changes nothing about it, so it survives
   * until a retry actually lands.
   */
  origin: 'local' | 'remote';
}

/** Copy for a `toPatch` that refused without saying why — see `validate`. */
export const REFUSED_WITHOUT_REASON =
  'That value cannot be saved. Check it and try again.';

/**
 * Turn a rejected save into a sentence a solicitor can act on.
 *
 * **Bound to `handleMutationError`, not a second copy of it** — that mapper
 * already owns this product's status-to-copy decisions (401 / 403 / 404 / 500),
 * and restating them here would give the same fact two definitions that drift.
 * Root `CLAUDE.md` § "Bind to the source of truth".
 *
 * It is bound to rather than merely called because it leaves two shapes as raw
 * wire text, and both are shapes this surface hits routinely:
 *
 * · **A dropped connection** arrives as `DirectApiError(status 0)` carrying the
 *   browser's own `Failed to fetch`, and a timeout as `status 408` carrying
 *   `Request timeout`. The mapper has no branch for either, so both fall
 *   through to `error.message`.
 * · **A class-validator 400** carries a wire property name the reader has never
 *   seen — `office must be a string` names a column, not the `Office` field she
 *   is looking at. The mapper returns 400s verbatim, which is right for a 400
 *   the backend wrote as a sentence and wrong for this.
 *
 * **The two 400s are told apart exactly, not by guessing at the prose.** Nest
 * sends class-validator failures as a `string[]` and hand-written errors as a
 * single string (see `ApiErrorResponse.message`), and `DirectApiError` keeps the
 * unjoined body on `response`. So a real backend sentence — *"This enquiry has
 * already been converted."* — still reaches the user untouched, which is the
 * one message in the reported set that already read as English.
 */
export function saveFailureMessage(error: unknown): string {
  if (error instanceof DirectApiError) {
    // `status 0` is this client's own marker for "the request never landed".
    if (error.status === 0) {
      return 'Your connection dropped, so this change was not sent. Check your connection and try again.';
    }
    if (error.status === 408) {
      return 'Saving this change took too long. Try again.';
    }
    if (error.status === 400 && Array.isArray(error.response?.message)) {
      return 'That value is not one this field accepts. Check it and try again.';
    }
  }
  return handleMutationError(error);
}

/** Reference equality is not enough — a composite draft is rebuilt per keystroke. */
export function sameDraft(a: AttributeCommitDraft, b: AttributeCommitDraft) {
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export interface AttributeCommit<TDraft extends AttributeCommitDraft> {
  /** Which field's editor is open, if any. */
  openIndex: number | null;
  /** The open field's live draft — local, so a keystroke costs no request. */
  draft: TDraft;
  /** The refused commit — rendered on the row it belongs to, or `null`. */
  failure: AttributeCommitFailure | null;
  /**
   * **The labels of every field whose save has been travelling longer than
   * {@link ATTRIBUTE_SAVE_INDICATOR_DELAY_MS}** (VALE-349).
   *
   * Labels rather than indices because the chain moves on: `Tab` advances
   * optimistically so a slow link never stalls it, so by the time a save is
   * worth reporting the reader is usually two rows further down. A label is what
   * still addresses the row that is actually saving.
   *
   * A field can hold more than one save at a time (edit, `Tab` back, edit
   * again), so it leaves this list when the **last** of them settles — see
   * `endSave`.
   */
  saving: string[];
  /** Per-keystroke draft update. */
  setDraft: (next: TDraft) => void;
  /** Open a field's editor from its read-out. */
  open: (index: number) => void;
  /** `Escape`, or a picker dismissed without choosing: restore and close. */
  revert: () => void;
  /**
   * Blur / `Enter` / a chosen option. Commits, and closes **only if the value
   * was accepted** — a refused value stays open with the typing intact.
   */
  commitAndClose: (index: number, value: TDraft) => void;
  /** `Tab` / `Shift+Tab`. Commits, and advances only if the value was accepted. */
  step: (index: number, value: TDraft, delta: number) => void;
  /**
   * **Commit whatever editor is open, without waiting to be dismissed**
   * (VALE-1274) — what a surface's own `Save` calls before it submits.
   *
   * Returns whether the value was accepted, on the same contract as
   * {@link commitAndClose}: `true` when there was nothing open, or the open
   * draft committed; `false` when it was refused, in which case the editor
   * stays open wearing the reason and the caller must not go on to submit.
   *
   * **Why a surface cannot rely on dismissal to do this.** A popover row holds
   * its value in a draft and commits when the popover closes, and until
   * `@radix-ui/react-popover` 1.1.23 that close happened on the `pointerdown`
   * of the outside click — early enough that a click on a `disabled` Save
   * re-enabled the button in time for that same gesture's `click` to land on
   * it. 1.1.23 defers the dismissal to the `click` instead (upstream's fix for
   * extension overlays swallowing outside clicks, radix-ui/primitives#3928),
   * and a `disabled` button never fires one — so the row never committed, Save
   * stayed disabled, and clicking it did nothing at all, for ever.
   *
   * The race was ours to be relying on, not Radix's to keep. Committing what is
   * on screen is `Save`'s job, so it is a call a surface makes rather than a
   * side effect of a dismissal it does not control.
   */
  flush: () => boolean;
  /**
   * **The row whose editor has just closed on an accepted commit, and whose
   * record prop has not caught up yet** (VALE-1773) — or `null`, which is the
   * state almost all of the time.
   *
   * A commit fires `onSave` without awaiting it and closes the editor in the
   * same tick, so the very next render puts the read-out back with the *host's*
   * pre-save `display` on it. The reader, who has been watching the popover's
   * trigger track their typing, therefore sees **new, old, new**. It is a
   * render-order gap rather than a latency: even a fully optimistic
   * `setQueryData` reaches subscribers through TanStack Query's
   * `notifyManager`, which batches later than the handler that closed the
   * editor, so no host speed closes it.
   *
   * `value` is what was committed, and the grid renders it through
   * `attributeDraftDisplay` in place of `field.display` until this clears — see
   * that module for why the fix is a formatter rather than holding the editor
   * open.
   *
   * It clears on whichever of these comes first:
   *
   * · **the record moved** — `fields[index].draft` is no longer what the field
   *   opened with, which is the prop-agnostic signal that the host has caught
   *   up. Exact-value equality is deliberately not the test: a host may
   *   normalise, and VALE-1350's derived keys and expanded relations mean the
   *   value coming back is not always the value sent;
   * · **the save settled** and the record still did not move — the host
   *   accepted a value that reads the same, so continuing to show the drafted
   *   one would be the lie this is meant to prevent. `onSave` must land the
   *   value in the record before it resolves (`CLAUDE.md` §6b / VALE-709), so
   *   by then there is nothing left to wait for;
   * · **the row re-opened**, including the re-open a refused save performs.
   *   The one exception is the reader re-opening it with `open` while the
   *   bridge still covers it (VALE-2043): the editor then opens on `value`
   *   rather than on the stale record, and the bridge stays so an `Escape`
   *   leaves the committed value on the row.
   */
  bridge: { index: number; value: TDraft } | null;
}

export function useAttributeCommit<
  TPatch,
  TDraft extends AttributeCommitDraft = AttributeCommitDraft
>({
  fields,
  onSave,
  onEditingChange,
  onUnsavedChange,
  onUncommittedDraftChange
}: {
  fields: AttributeCommitField<TPatch, TDraft>[];
  /** Sends the patch. Rejecting is what surfaces the failure. */
  onSave: (patch: TPatch) => Promise<unknown>;
  onEditingChange?: (editing: boolean) => void;
  /**
   * **Whether this field list is holding state the commit path could not
   * store** — a refused value, or a save that has been travelling longer than
   * {@link ATTRIBUTE_SAVE_INDICATOR_DELAY_MS} (VALE-349).
   *
   * It is what lets a drawer stop and ask before closing over an edit that was
   * never stored, and it is the *same* threshold the indicator uses, so a
   * drawer prompts exactly when the reader can see a save is unfinished. A
   * clean edit still commits silently on close (VALE-60): the commit the close
   * gesture itself fires is 0ms old and never reaches this.
   *
   * **Called synchronously**, beside the state write rather than from an
   * effect, for the same reason `draftRef` and `openIndexRef` are written that
   * way below — and here it is load-bearing rather than merely consistent.
   * Radix's dismissable layer detects an outside click on `pointerdown` and
   * dismisses in that same dispatch, so a refusal produced by the blur that
   * click causes has to reach the host *before* React re-renders or the drawer
   * closes over it.
   */
  onUnsavedChange?: (unsaved: boolean) => void;
  /**
   * **Whether an editor is open holding a draft the reader has changed**
   * (VALE-1274) — a value that is on screen and in no record yet.
   *
   * Distinct from {@link onUnsavedChange}, and deliberately not folded into it:
   * that one answers *"would closing lose something the commit path already
   * refused or is still sending"*, and it drives a leave guard. This one
   * answers *"is there anything to save"*, and it drives a buffered create
   * surface's `Save` button. A surface whose `Save` is disabled until a row
   * commits, on a row that commits only when its popover is dismissed, cannot
   * be reached by the click that would dismiss it — see {@link
   * AttributeCommit.flush}.
   *
   * Reported synchronously beside the ref writes, for the reason the whole hook
   * is ref-first: the answer has to be right inside the gesture that reads it.
   */
  onUncommittedDraftChange?: (holding: boolean) => void;
}): AttributeCommit<TDraft> {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<TDraft>('' as TDraft);
  const [failure, setFailure] = useState<AttributeCommitFailure | null>(null);
  const [saving, setSaving] = useState<string[]>([]);
  /** See {@link AttributeCommit.bridge} (VALE-1773). */
  const [bridge, setBridge] = useState<{
    index: number;
    label: string;
    value: TDraft;
    baseline: TDraft;
  } | null>(null);
  /*
    Mirrored, because `endSave` is reached from a settled promise and must not
    be a dependency of the commit path — the same ref-first arrangement every
    other asynchronous reader in this hook uses.
  */
  const bridgeRef = useRef<typeof bridge>(null);
  /** Cleared on unmount with the save-indicator timers, for the same reason. */
  const bridgeReleaseTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set()
  );
  const writeBridge = useCallback((next: typeof bridge) => {
    bridgeRef.current = next;
    setBridge(next);
  }, []);

  /** What the open field held when it opened — what `Escape` restores. */
  const openedWithRef = useRef<TDraft>('' as TDraft);
  /**
   * What the **record** held when the open field opened — the baseline a bridge
   * set by this field's next commit compares against (VALE-2043).
   *
   * The same as `openedWithRef` except when a row is re-opened under its own
   * bridge: the editor then opens on the committed value, but the record is
   * still the stale one, and "has the record caught up?" has to be asked of
   * the record.
   */
  const openedRecordRef = useRef<TDraft>('' as TDraft);
  /**
   * Set while `Tab` is handing over. Removing a focused input fires `blur`,
   * which would otherwise commit-and-close the editor we just opened.
   */
  const steppingRef = useRef(false);
  /**
   * Live mirrors of the two pieces of state the **asynchronous** rejection
   * handler has to read.
   *
   * It cannot read the state itself: `draft` and `openIndex` closed over when
   * the request was sent are stale by the time it settles, and being stale is
   * precisely the bug — the handler's whole job is to decide whether the user
   * has since moved on and started typing somewhere else. Written synchronously
   * beside each `setState` rather than in an effect, because a rejection can
   * land before an effect has run.
   */
  const draftRef = useRef<TDraft>('' as TDraft);
  const openIndexRef = useRef<number | null>(null);

  /**
   * The uncommitted-draft machinery (VALE-1274), ref-first for the same reason
   * the unsaved-state machinery below is: {@link onUncommittedDraftChange}
   * gates a `Save` button, and the answer has to be right during the gesture
   * that is about to read it.
   */
  const uncommittedRef = useRef(false);
  const onUncommittedDraftChangeRef = useRef(onUncommittedDraftChange);
  useEffect(() => {
    onUncommittedDraftChangeRef.current = onUncommittedDraftChange;
  });

  /** Recompute, and report only a genuine transition. */
  const syncUncommitted = useCallback(() => {
    const next =
      openIndexRef.current !== null &&
      !sameDraft(draftRef.current, openedWithRef.current);
    if (next === uncommittedRef.current) return;
    uncommittedRef.current = next;
    onUncommittedDraftChangeRef.current?.(next);
  }, []);

  const writeDraft = useCallback(
    (next: TDraft) => {
      draftRef.current = next;
      setDraft(next);
      syncUncommitted();
    },
    [syncUncommitted]
  );

  /**
   * The unsaved-state machinery (VALE-349). All of it is ref-first for the
   * reason {@link onUnsavedChange} documents: the answer has to be right
   * *during* the `pointerdown` that is closing the drawer, which is before any
   * render or effect this hook could hang it on.
   */
  const failureRef = useRef<AttributeCommitFailure | null>(null);
  /**
   * Per-label in-flight bookkeeping: how many saves that field is holding, and
   * the timer that will promote it to {@link AttributeCommit.saving} if the
   * first of them is still travelling at the threshold.
   */
  const inFlightRef = useRef(
    new Map<
      string,
      { count: number; timer: ReturnType<typeof setTimeout> | null }
    >()
  );
  const savingRef = useRef<Set<string>>(new Set());
  const unsavedRef = useRef(false);
  /*
    Read through a ref so a host that rebuilds its callback every render — every
    host, since these props are inline arrows — does not have to be a dependency
    of the commit path itself.
  */
  const onUnsavedChangeRef = useRef(onUnsavedChange);
  useEffect(() => {
    onUnsavedChangeRef.current = onUnsavedChange;
  });

  /** Recompute, and report only a genuine transition. */
  const syncUnsaved = useCallback(() => {
    const next = failureRef.current !== null || savingRef.current.size > 0;
    if (next === unsavedRef.current) return;
    unsavedRef.current = next;
    onUnsavedChangeRef.current?.(next);
  }, []);

  /**
   * The one way this hook writes a failure — ref and state together, then the
   * report. Every `setFailure` call site goes through it, so "is this grid
   * holding a refusal" can never be answered two different ways.
   */
  const writeFailure = useCallback(
    (next: AttributeCommitFailure | null) => {
      failureRef.current = next;
      setFailure(next);
      syncUnsaved();
    },
    [syncUnsaved]
  );

  const beginSave = useCallback(
    (label: string) => {
      const entry = inFlightRef.current.get(label) ?? { count: 0, timer: null };
      entry.count += 1;
      inFlightRef.current.set(label, entry);
      // Only the first save of a run arms the timer; a second one landing on the
      // same row inherits the countdown the first started rather than resetting
      // it, so a row cannot stay silent by being edited repeatedly.
      if (entry.timer === null && !savingRef.current.has(label)) {
        entry.timer = setTimeout(() => {
          entry.timer = null;
          savingRef.current.add(label);
          setSaving((current) =>
            current.includes(label) ? current : [...current, label]
          );
          syncUnsaved();
        }, ATTRIBUTE_SAVE_INDICATOR_DELAY_MS);
      }
    },
    [syncUnsaved]
  );

  /** Settled, whichever way it settled — the indicator says nothing about how. */
  const endSave = useCallback(
    (label: string) => {
      const entry = inFlightRef.current.get(label);
      if (!entry) return;
      entry.count -= 1;
      if (entry.count > 0) return;
      if (entry.timer !== null) clearTimeout(entry.timer);
      inFlightRef.current.delete(label);
      if (savingRef.current.delete(label)) {
        setSaving((current) => current.filter((held) => held !== label));
      }
      /*
        The bridge's terminating release (VALE-1773). Its ordinary release is
        the record moving, which is the common case and lands first; this is the
        one that guarantees it ends at all. `onSave` must have the value in the
        record before it resolves (`CLAUDE.md` §6b / VALE-709), so a settled
        save whose prop still reads as it did means the host accepted something
        that looks the same — and going on showing the drafted value would then
        be the misreport the bridge exists to prevent. It covers a *rejected*
        save too, where the row must go back to the record while
        `handleRejection` re-opens it over the top.

        **Deferred by a macrotask, and that is the whole of why this is not one
        line.** Settling is not rendering: the host writes the record inside its
        own `onSave`, and TanStack Query hands that write to subscribers through
        `notifyManager`, which schedules on a `setTimeout(0)` of its own.
        Releasing synchronously here would drop the bridge in the window between
        the write and the render that carries it — putting back the single stale
        frame this whole mechanism removes. Queued after theirs, so theirs runs
        first and the record-moved release above almost always wins the race
        anyway; this only ever fires when it did not.
      */
      if (bridgeRef.current?.label === label) {
        const timer = setTimeout(() => {
          bridgeReleaseTimersRef.current.delete(timer);
          if (bridgeRef.current?.label === label) writeBridge(null);
        }, 0);
        bridgeReleaseTimersRef.current.add(timer);
      }
      syncUnsaved();
    },
    [syncUnsaved, writeBridge]
  );

  /*
    A surface can unmount with saves still travelling — a drawer closed over one
    is exactly the case this ticket is about. The requests are left alone (they
    are the host's to care about); only the timers that would set state on a
    dead component are cleared.
  */
  useEffect(() => {
    const held = inFlightRef.current;
    const bridgeTimers = bridgeReleaseTimersRef.current;
    return () => {
      held.forEach((entry) => {
        if (entry.timer !== null) clearTimeout(entry.timer);
      });
      held.clear();
      bridgeTimers.forEach((timer) => clearTimeout(timer));
      bridgeTimers.clear();
    };
  }, []);

  /*
    **The bridge's ordinary release: the record moved** (VALE-1773). Divergence
    from the value the field opened with, rather than equality with the value
    committed, because equality is not always attainable — a host may normalise,
    and VALE-1350's derived keys and expanded relations mean what comes back is
    not always what went out. Divergence answers the question actually being
    asked, which is "has the prop stopped being the stale one?".

    **Derived during render rather than released from an effect.** An effect
    would repaint the stale frame once before clearing — which is the very thing
    this mechanism removes — and would also be a `setState` in an effect, which
    this package's lint rule refuses for that reason. The stored state stays as
    it was; what the grid reads is this. The stored copy is tidied up by the
    settle timer in `endSave`, and a row that has disappeared from `fields`
    releases here too, since there is nothing left to draw the value on.
  */
  const activeBridge =
    bridge &&
    fields[bridge.index] &&
    sameDraft(fields[bridge.index].draft, bridge.baseline)
      ? bridge
      : null;

  /**
   * `clearFailure` is `false` for exactly one caller — the rejection handler
   * below, which re-opens a field *because* it failed and would otherwise wipe
   * the message it is about to show.
   */
  const openField = useCallback(
    (
      index: number,
      initial: TDraft | undefined,
      clearFailure: boolean,
      fromBridge = false
    ) => {
      /*
        **A row re-opened before its record caught up opens on what was
        committed, not on the record** (VALE-2043). The bridge is already drawing
        the committed value on the row, so seeding from the stale record put the
        pre-commit value in the editor under a read-out that said otherwise. A
        server-derived member (the inventory figure's `Confirmed`) cannot be
        written optimistically, so this window lasts the whole round trip, and
        closing the popover after any other edit re-sent the stale member and
        silently undid the commit.

        Only for the reader's own `open`. The rejection handler re-opens on the
        refused value against the record, which is what makes a retry a change;
        and `Tab` stays on the record because a live boolean's `onStep` commits
        `field.draft`, which would then read as a change back to the stale value.
      */
      const held = bridgeRef.current;
      const bridged =
        fromBridge &&
        held !== null &&
        held.index === index &&
        sameDraft(fields[index].draft, held.baseline)
          ? held
          : null;
      openedWithRef.current = bridged ? bridged.value : fields[index].draft;
      openedRecordRef.current = bridged
        ? bridged.baseline
        : fields[index].draft;
      writeDraft(initial ?? openedWithRef.current);
      openIndexRef.current = index;
      setOpenIndex(index);
      // Defect 2: engaging with the grid again clears a stale failure. The old
      // code only ever cleared it inside a *successful* commit, so a message
      // about a field the user had since fixed sat over a form that was fine.
      if (clearFailure) writeFailure(null);
      /*
        A row being edited draws its own value, so **this row's** bridge is
        released (VALE-1773). The case that matters is the rejection handler's
        re-open, where the bridge would otherwise keep the refused value on a
        row whose record never took it.

        Scoped to the index rather than clearing outright, because `Tab` opens
        the *next* row immediately after committing this one — and that row's
        neighbour is exactly the read-out the bridge was set for.

        Kept when the editor was seeded from it (VALE-2043): the record is still
        stale, so an `Escape` would otherwise put the pre-commit value back on
        the row. It still ends the ordinary way, on the record moving or the
        save settling.
      */
      if (!bridged && bridgeRef.current?.index === index) writeBridge(null);
      /*
        After `openIndexRef`, not inside `writeDraft` above — the answer reads
        both refs, and the write order here is `openedWith` → draft → index.
      */
      syncUncommitted();
      onEditingChange?.(true);
    },
    [
      fields,
      onEditingChange,
      syncUncommitted,
      writeBridge,
      writeDraft,
      writeFailure
    ]
  );

  /**
   * Close the editor and say nothing about the failure — see {@link shut} for
   * the arm that forgets it.
   *
   * **The split is what makes a refusal survive the gesture that closes a
   * drawer** (VALE-349). Radix's dismissable layer blurs the open input on its
   * way out, which commits — a retry of the value the server just refused. If
   * closing the editor also wiped the refusal, the surface would report itself
   * clean in the same dispatch the drawer is closing in, and the leave guard
   * would wave through exactly the case it exists to stop.
   */
  const closeEditor = useCallback(() => {
    openIndexRef.current = null;
    setOpenIndex(null);
    syncUncommitted();
    onEditingChange?.(false);
  }, [onEditingChange, syncUncommitted]);

  /** Close **and** forget the failure — engaging with the grid again (§2). */
  const shut = useCallback(() => {
    writeFailure(null);
    closeEditor();
  }, [closeEditor, writeFailure]);

  const open = useCallback(
    (index: number) => openField(index, undefined, true, true),
    [openField]
  );

  const revert = useCallback(() => {
    writeDraft(openedWithRef.current);
    shut();
  }, [shut, writeDraft]);

  /**
   * A save that came back rejected, a round-trip after the chain moved on.
   *
   * **Re-open the failed field only when doing so destroys nothing.** The
   * treatment's contract is that a rejected commit stays open and says why
   * (§2), and bringing the field back with the typing still in it is the whole
   * reason a reverted-looking value cannot be mistaken for a save that worked.
   * But the draft is one piece of state, so re-opening field N *replaces*
   * whatever is in the editor the user has since moved to — which is how a
   * `EDIT_B_TYPED_WHILE_SAVING` disappeared with no message at all.
   *
   * So the two halves are separated. The message is unconditional: the failure
   * is always reported. Moving the user is not: it happens only while the open
   * editor is untouched, which is every case the contract was written for
   * (`Tab`, then wait) and none of the cases where it costs the user a
   * correction (`Tab`, then type).
   */
  const handleRejection = useCallback(
    (index: number, label: string, failedDraft: TDraft, error: unknown) => {
      const editorIsDirty =
        openIndexRef.current !== null &&
        !sameDraft(draftRef.current, openedWithRef.current);

      if (!editorIsDirty) openField(index, failedDraft, false);
      writeFailure({
        label,
        message: saveFailureMessage(error),
        origin: 'remote'
      });
    },
    [openField, writeFailure]
  );

  /**
   * Send the draft if it changed, and report whether the value was **accepted**.
   *
   * The boolean is what lets `Tab` refuse to advance off a value the user is
   * being told is wrong (§2). A synchronous refusal is known before the request
   * would leave, so stepping away from it — or closing the editor over it —
   * throws the reader's correction away in front of them. A *server* rejection
   * cannot work that way: it arrives a round-trip later, which is why the chain
   * still advances optimistically and {@link handleRejection} deals with the
   * aftermath.
   */
  const commit = useCallback(
    (index: number, value: TDraft): boolean => {
      const field = fields[index];
      /*
        Nothing changed — not a refusal, and nothing to send.

        **But a LOCAL refusal this row is still showing has to go** (VALE-1476).
        A refusal is only ever cleared in two places: when a sent patch lands,
        and when the row is re-opened. This path sends nothing, so the first
        never fires, and the row stayed red over a draft the reviewer had
        already put back — clearing only when they clicked into the field
        again, which reads as the correction not registering. A `remote`
        refusal is left alone: it is about the value the row actually holds,
        which reverting the editor does not change (VALE-349).
      */
      if (sameDraft(value, openedWithRef.current)) {
        if (
          failureRef.current?.origin === 'local' &&
          failureRef.current.label === field.label
        ) {
          writeFailure(null);
        }
        return true;
      }

      // [INVDRAW-3] / VALE-74: a sibling's *currently committed* draft, read
      // by label, scoped to this same `fields` list — never another block's.
      const readSibling: AttributeSiblingReader<TDraft> = (label) =>
        fields.find((sibling) => sibling.label === label)?.draft;

      // Defect 4, first route: a reason the reader can act on.
      const reason = field.validate?.(value, readSibling) ?? null;
      if (reason) {
        writeFailure({ label: field.label, message: reason, origin: 'local' });
        return false;
      }

      // A row that commits to the host rather than to the record — see
      // `localCommit`. Deliberately *after* `validate`, so such a row can still
      // refuse a value with a reason, and *before* `toPatch`, which it replaces.
      if (field.localCommit) {
        field.localCommit(value);
        writeFailure(null);
        return true;
      }

      // Defect 4, second route: a `null` patch is the older, wordless way a
      // spec says no. The old code returned here with no message and let the
      // editor close over the reverted value, which looked exactly like a save
      // that worked.
      // `=== null`, not falsiness: `toPatch` advertises `TPatch | null`, and
      // this hook is generic and exported. A surface whose patch type is a
      // primitive would have a perfectly valid `0` / `''` / `false` refused.
      const patch = field.toPatch(value);
      if (patch === null) {
        writeFailure({
          label: field.label,
          message: REFUSED_WITHOUT_REASON,
          origin: 'local'
        });
        return false;
      }

      const label = field.label;
      /*
        **A retry of a field that is currently being refused keeps the refusal
        until the retry actually lands** (VALE-349). Clearing it here — which is
        what every other commit does, optimistically — would mean the blur a
        closing drawer fires reports the surface clean in the same dispatch the
        drawer is closing in, and the reader walks away from a value that was
        refused a moment ago with no prompt at all. Any *other* standing failure
        is left alone too: another row being broken is not this commit's news to
        clear, and opening a field already clears a stale one (defect 2).
      */
      if (failureRef.current === null) writeFailure(null);
      /*
        The row reports itself as saving from here — after the two synchronous
        refusals above, which never leave, and before the send, because `onSave`
        can settle inside the same tick on a mocked or cached client and
        `endSave` must have something to end.
      */
      beginSave(label);
      /*
        **Hold the committed value on the row until the record catches up**
        (VALE-1773) — see {@link AttributeCommit.bridge}.

        Here rather than in `commitAndClose`, so every path that commits and
        then stops drawing the editor is covered by construction: the popover's
        dismiss and confirm, `Enter`, `Tab`'s step off the row, and a surface's
        own `Save` calling `flush`. Each of those leaves a read-out where a live
        editor was, and each therefore had the stale frame.

        Only on the sent path. A `localCommit` row writes to the host
        synchronously and re-renders in the same batch, so it has no gap; the
        two refusals above never reach here at all; and an unchanged value
        returned long before this line. That is also what makes the release in
        `endSave` total — every bridge set here has a save that will settle.
      */
      writeBridge({
        index,
        label,
        value,
        baseline: openedRecordRef.current
      });
      void onSave(patch)
        .then(() => {
          // The landed retry is what clears the refusal it was retrying.
          if (failureRef.current?.label === label) writeFailure(null);
        })
        .catch((error: unknown) => {
          handleRejection(index, label, value, error);
        })
        .finally(() => {
          endSave(label);
        });
      return true;
    },
    [
      beginSave,
      endSave,
      fields,
      handleRejection,
      onSave,
      writeBridge,
      writeFailure
    ]
  );

  const commitAndClose = useCallback(
    (index: number, value: TDraft) => {
      const accepted = commit(index, value);
      /*
        `closeEditor`, not `shut`: an accepted commit does not decide whether the
        row is still holding a refusal — `commit` does, and a retry still in the
        air keeps one. See `closeEditor`.
      */
      if (accepted && !steppingRef.current) closeEditor();
    },
    [closeEditor, commit]
  );

  /**
   * See {@link AttributeCommit.flush}. Reads the live refs rather than taking
   * an index and a value, because the caller is the *host's* `Save` handler,
   * which has no idea which row is open or what is in it.
   *
   * `steppingRef` is deliberately not consulted: it exists to stop a blur from
   * closing an editor `Tab` has just opened, and a `Save` press is not that.
   */
  const flush = useCallback((): boolean => {
    const index = openIndexRef.current;
    if (index === null) return true;
    const accepted = commit(index, draftRef.current);
    if (accepted) closeEditor();
    return accepted;
  }, [closeEditor, commit]);

  const step = useCallback(
    (index: number, value: TDraft, delta: number) => {
      if (!commit(index, value)) return;

      /*
        Step *over* a `readOnly` row rather than onto it. It renders as a span
        with nothing to open, so landing there would close the chain silently in
        the middle of a record — `Tab` out of the field above it would look like
        the surface had simply stopped responding.

        A loop rather than a single skip, because two read-only rows can sit
        next to each other, and it terminates on the same bounds check below.
      */
      let next = index + delta;
      while (next >= 0 && next < fields.length && fields[next]?.readOnly) {
        next += delta;
      }
      if (next < 0 || next >= fields.length) {
        shut();
        return;
      }
      steppingRef.current = true;
      openField(next, undefined, true);
      // Cleared once the blur from the unmounting input has had its chance.
      setTimeout(() => {
        steppingRef.current = false;
      }, 0);
    },
    [commit, fields, openField, shut]
  );

  return {
    openIndex,
    draft,
    failure,
    saving,
    bridge: activeBridge && {
      index: activeBridge.index,
      value: activeBridge.value
    },
    setDraft: writeDraft,
    open,
    revert,
    commitAndClose,
    step,
    flush
  };
}
